import type { UsageReport } from "./api";
import { MODEL_COLORS, OTHER_MODEL } from "./timeline";

/** Every day of the range, oldest first, with spend by model (a quiet day is a gap, not missing). */
export function dailyByModel(r: UsageReport) {
  const byDate = new Map(r.days.map((d) => [d.date, d]));
  const out: Array<{ date: string; total: number; requests: number; models: Record<string, number> }> = [];
  for (let t = Date.parse(r.start); t <= Date.parse(r.end); t += 86_400_000) {
    const date = new Date(t).toISOString().slice(0, 10);
    const d = byDate.get(date);
    out.push({ date, total: d?.spend ?? 0, requests: d?.requests ?? 0, models: d?.models ?? {} });
  }
  return out;
}

export interface Series { key: string; label: string; color: string }

/**
 * The three models with the most spend in the range keep their own colour (the same slots the
 * session timeline uses for models); the rest fold into "other". Colour follows the model's rank
 * in the range shown, and the legend names every colour.
 */
export function modelSeries(r: UsageReport): Series[] {
  const top = r.byModel.filter((m) => m.spend > 0).slice(0, MODEL_COLORS.length);
  const out: Series[] = top.map((m, i) => ({ key: m.model, label: m.model, color: MODEL_COLORS[i]! }));
  if (r.byModel.filter((m) => m.spend > 0).length > top.length) out.push({ key: "__other", label: "other models", color: OTHER_MODEL });
  return out;
}

export interface StackSegment { key: string; y: number; h: number; value: number }

/** Stacked columns, bottom up in series order, with a 2px surface gap between segments. */
export function stackColumns(days: ReturnType<typeof dailyByModel>, series: Series[], height: number) {
  const named = new Set(series.filter((s) => s.key !== "__other").map((s) => s.key));
  const max = Math.max(1e-9, ...days.map((d) => d.total));
  return {
    max,
    columns: days.map((d) => {
      const values = series.map((s) => ({
        key: s.key,
        value: s.key === "__other"
          ? Object.entries(d.models).filter(([m]) => !named.has(m)).reduce((a, [, v]) => a + v, 0)
          : d.models[s.key] ?? 0,
      }));
      let y = height;
      const segments: StackSegment[] = [];
      for (const v of values) {
        if (v.value <= 0) continue;
        const h = (v.value / max) * height;
        const gap = segments.length && h > 4 ? 2 : 0;
        segments.push({ key: v.key, y: y - h + gap, h: Math.max(0.5, h - gap), value: v.value });
        y -= h;
      }
      return { date: d.date, total: d.total, requests: d.requests, segments };
    }),
  };
}

/** Sequential blue, light to dark (dataviz reference ramp); the lightest step still clears 2:1. */
export const HEAT_STEPS = 5;

export interface HeatCell { day: string; hour: number; requests: number; spend: number; step: number }

/**
 * Hourly buckets (UTC hours) placed in the viewer's own time zone: one row per local day, the
 * last `days` days ending today, 24 columns. Empty hours are step -1 (drawn as the surface).
 */
export function heatGrid(buckets: Array<{ hour: string; requests: number; spend: number }>, days: number, now = new Date(), tzOffsetMin = now.getTimezoneOffset()) {
  const local = (t: number) => new Date(t - tzOffsetMin * 60_000);
  const dayKey = (t: number) => local(t).toISOString().slice(0, 10);
  const today = dayKey(now.getTime());
  const rows: string[] = [];
  for (let i = days - 1; i >= 0; i--) rows.push(new Date(Date.parse(`${today}T00:00:00Z`) - i * 86_400_000).toISOString().slice(0, 10));
  const cells = new Map<string, HeatCell>();
  for (const r of rows) for (let h = 0; h < 24; h++) cells.set(`${r}|${h}`, { day: r, hour: h, requests: 0, spend: 0, step: -1 });
  for (const b of buckets) {
    const t = Date.parse(`${b.hour}:00:00Z`);
    const c = cells.get(`${dayKey(t)}|${local(t).getUTCHours()}`);
    if (!c) continue;
    c.requests += b.requests;
    c.spend += b.spend;
  }
  const max = Math.max(0, ...[...cells.values()].map((c) => c.requests));
  for (const c of cells.values()) {
    if (c.requests > 0) c.step = Math.min(HEAT_STEPS - 1, Math.floor((c.requests / max) * HEAT_STEPS));
  }
  return { rows, cells, max };
}
